import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { usePageMeta, shortDate } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import PostCard from "./PostCard";
import ReportDialog from "./ReportDialog";
import { toast } from "sonner";
import { ChevronLeft, ArrowUp, Trash2, Library, CheckCircle2, Lock, Circle } from "lucide-react";

export default function FeedDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [post, setPost] = useState(undefined);
  const [comments, setComments] = useState([]);
  const [series, setSeries] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [reportOn, setReportOn] = useState(null);
  usePageMeta(post?.title || t("post_details"));

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await api.get(`/feed/post/${id}`);
      setPost(data);
      const { data: cs } = await api.get(`/feed/post/${id}/comments`);
      setComments(Array.isArray(cs) ? cs : []);
      api.post(`/feed/post/${id}/view`).catch(() => {});
      if (data?.series_id) {
        api.get(`/feed/series/${data.series_id}`).then((r) => setSeries(r.data)).catch(() => setSeries(null));
      } else setSeries(null);
    } catch { setPost(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const unlock = async (p) => {
    try { await api.post(`/feed/unlock/${p.id}`); await load(); }
    catch (e) {
      const status = e?.response?.status;
      if (status === 402) { if (window.confirm(t("feed_credits_needed", { n: p.price_credits ?? 0 }))) navigate("/app/credits"); }
      else toast.error(apiError(e));
    }
  };

  const send = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try { const { data } = await api.post(`/feed/post/${id}/comments`, { body }); setText(""); setComments((c) => [...c, data]); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const removeComment = async (c) => {
    try { await api.delete(`/feed/post/${id}/comments/${c.id}`); setComments((prev) => prev.filter((x) => x.id !== c.id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (post === undefined) return <div className="py-16"><Spinner label={t("common.loading")} /></div>;
  if (post === null) return (
    <div className="mx-auto max-w-2xl py-16 text-center">
      <p className="text-sm italic text-muted-foreground">{t("post_not_found")}</p>
      <button onClick={() => navigate("/app/feed")} className="mt-4 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">{t("back")}</button>
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="feed-detail-screen">
      <button onClick={() => navigate(-1)} data-testid="feed-detail-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> {t("back")}
      </button>

      <PostCard post={post} onReport={setReportOn} onUnlock={unlock} />

      {series && series.parts?.length > 0 ? (
        <div className="rounded-2xl border border-border bg-primary/5 p-4" data-testid="series-box">
          <div className="mb-2 flex items-center gap-2">
            <Library className="h-4 w-4 text-primary" />
            <p className="flex-1 truncate text-sm font-semibold text-foreground">{series.name}</p>
            <span className="text-xs text-muted-foreground">{t("series_progress", { viewed: series.parts.filter((p) => p.viewed).length, total: series.parts.length })}</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {series.parts.map((part) => {
              const current = part.id === post.id;
              return (
                <button key={part.id} disabled={current} onClick={() => navigate(`/app/feed/post/${part.id}`)} data-testid={`series-part-${part.id}`}
                  className={`flex w-28 shrink-0 flex-col gap-1 rounded-xl border p-2 text-left ${current ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"} ${!part.unlocked ? "opacity-70" : ""}`}>
                  <span className="flex items-center justify-between text-xs font-semibold">{part.series_position ?? "—"}
                    {!part.unlocked ? <Lock className="h-3 w-3" /> : part.viewed ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
                  </span>
                  <span className="line-clamp-2 text-[11px] leading-tight">{part.title || t("post_details")}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl border border-border bg-card p-4" data-testid="feed-detail-comments">
        <p className="mb-3 text-sm font-semibold text-foreground">{t("comment_sheet_title", { n: comments.length })}</p>
        {comments.length === 0 ? (
          <p className="py-6 text-center text-sm italic text-muted-foreground">{t("comment_empty")}</p>
        ) : (
          <div className="space-y-3">
            {comments.map((c) => {
              const canDelete = user?.id === c.user_id || user?.role === "admin" || user?.id === post.coach_id;
              return (
                <div key={c.id} className="flex items-start gap-2.5" data-testid={`comment-${c.id}`}>
                  {c.user_photo ? <img src={c.user_photo} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" /> : <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">{(c.user_name || c.author_name || "·")[0]}</span>}
                  <div className="min-w-0 flex-1 rounded-2xl bg-secondary/40 px-3 py-2">
                    <div className="flex items-baseline gap-2">
                      <p className="truncate text-xs font-semibold text-foreground">{c.user_name || c.author_name || t("coach")}</p>
                      {c.created_at ? <p className="text-[10px] text-muted-foreground">{shortDate(c.created_at)}</p> : null}
                    </div>
                    <p className="text-sm text-foreground">{c.body}</p>
                  </div>
                  {canDelete ? <button onClick={() => removeComment(c)} data-testid={`comment-delete-${c.id}`} className="rounded p-1 text-muted-foreground hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button> : null}
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-3 flex items-end gap-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={t("comment_input_placeholder")} rows={1} data-testid="feed-detail-comment-input" className="max-h-28 min-h-[44px] flex-1 resize-none rounded-2xl border border-border bg-background px-3.5 py-2.5 text-sm outline-none focus:border-primary" />
          <button onClick={send} disabled={!text.trim() || sending} data-testid="feed-detail-comment-send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"><ArrowUp className="h-5 w-5" /></button>
        </div>
      </div>

      <ReportDialog post={reportOn} open={!!reportOn} onClose={() => setReportOn(null)} />
    </div>
  );
}
